import { PAYMENT_METHOD } from '@monorepo/shared/schemas/common'
import type { Invoice } from '@monorepo/shared/schemas/invoices'
import { Calendar, Download, Loader2 } from 'lucide-react'
import { useState } from 'react'
import {
  formatCentsAsCurrency,
  formatInvoiceDate,
  invoicesService,
  paymentsService,
} from '@/features/invoices'
import { InvoiceStatusActions } from '@/features/invoices/components/InvoiceStatusActions'
import { RecordPaymentDialog } from '@/features/invoices/components/RecordPaymentDialog'
import { useInvoicePdf } from '@/features/invoices/hooks/useInvoicePdf'
import { toast } from '@/lib/toast'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'

const formatLabel = (value: string) =>
  value
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')

interface InvoiceDetailContentProps {
  invoice: Invoice
  onInvoiceUpdated: () => void
}

export function InvoiceDetailContent({
  invoice,
  onInvoiceUpdated,
}: InvoiceDetailContentProps) {
  const [loading, setLoading] = useState(false)
  const [showRecordPayment, setShowRecordPayment] = useState(false)
  const { downloadPdf, isGenerating } = useInvoicePdf()

  const handleFinalize = async () => {
    if (
      !confirm(
        `Finalize invoice ${invoice.invoice_number}? This cannot be undone.`
      )
    ) {
      return
    }
    setLoading(true)
    try {
      await invoicesService.finalizeInvoice(invoice.id)
      toast.success('Invoice finalized successfully')
      onInvoiceUpdated()
    } catch {
      toast.error('Failed to finalize invoice')
    } finally {
      setLoading(false)
    }
  }

  const handleMarkAsPaid = async () => {
    if (!confirm(`Mark invoice ${invoice.invoice_number} as fully paid?`)) {
      return
    }
    setLoading(true)
    try {
      await paymentsService.recordPayment({
        invoice_id: invoice.id,
        amount: invoice.amount_due_cents / 100,
        currency: invoice.currency,
        payment_method: PAYMENT_METHOD.other,
        notes: 'Marked as paid',
      })
      toast.success('Invoice marked as paid')
      onInvoiceUpdated()
    } catch {
      toast.error('Failed to mark invoice as paid')
    } finally {
      setLoading(false)
    }
  }

  const handleVoid = async () => {
    const reason = prompt('Reason for voiding this invoice:')
    if (!reason) {
      return
    }
    setLoading(true)
    try {
      await invoicesService.voidInvoice(invoice.id, { reason })
      toast.success('Invoice voided successfully')
      onInvoiceUpdated()
    } catch {
      toast.error('Failed to void invoice')
    } finally {
      setLoading(false)
    }
  }

  const lineItems = invoice.line_items || []
  const currency = invoice.currency

  return (
    <>
      <div className='grid grid-cols-1 lg:grid-cols-5 gap-6'>
        {/* Left Column — Invoice Preview (65%) */}
        <div className='lg:col-span-3 space-y-6'>
          {/* Invoice Header */}
          <Card>
            <CardContent className='p-6'>
              <div className='flex items-center justify-between mb-6'>
                <div>
                  <h2 className='text-2xl font-bold'>
                    {invoice.invoice_number}
                  </h2>
                  <div className='flex items-center gap-2 mt-2'>
                    <Badge variant='secondary'>
                      {formatLabel(invoice.status)}
                    </Badge>
                    <Badge
                      variant={
                        invoice.payment_status === 'paid'
                          ? 'default'
                          : 'outline'
                      }
                    >
                      {formatLabel(invoice.payment_status)}
                    </Badge>
                  </div>
                </div>
                <div className='text-right'>
                  <p className='text-3xl font-bold'>
                    {formatCentsAsCurrency(invoice.total_cents, currency)}
                  </p>
                  <p className='text-sm text-muted-foreground'>Total Amount</p>
                </div>
              </div>

              {/* From / Bill To */}
              <div className='grid grid-cols-2 gap-6 py-4 border-t'>
                <div>
                  <p className='text-sm font-medium text-muted-foreground mb-1'>
                    From
                  </p>
                  <p className='font-medium'>Your Company</p>
                </div>
                <div>
                  <p className='text-sm font-medium text-muted-foreground mb-1'>
                    Bill To
                  </p>
                  <p className='font-medium'>
                    {invoice.customer?.name || 'Unknown Customer'}
                  </p>
                  {invoice.customer?.email && (
                    <p className='text-sm text-muted-foreground'>
                      {invoice.customer.email}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Line Items Table */}
          <Card>
            <CardHeader className='pb-3'>
              <CardTitle className='text-sm font-medium'>Line Items</CardTitle>
            </CardHeader>
            <CardContent>
              <div className='overflow-x-auto'>
                <table className='w-full'>
                  <thead>
                    <tr className='border-b text-sm'>
                      <th className='text-left py-2 px-2 font-medium'>
                        Description
                      </th>
                      <th className='text-left py-2 px-2 font-medium'>Agent</th>
                      <th className='text-left py-2 px-2 font-medium'>
                        Feature
                      </th>
                      <th className='text-right py-2 px-2 font-medium'>Qty</th>
                      <th className='text-right py-2 px-2 font-medium'>
                        Unit Price
                      </th>
                      <th className='text-right py-2 px-2 font-medium'>
                        Discount
                      </th>
                      <th className='text-right py-2 px-2 font-medium'>
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((item, index) => (
                      <tr key={item.id || index} className='border-b text-sm'>
                        <td className='py-3 px-2'>{item.description}</td>
                        <td className='py-3 px-2 text-muted-foreground'>
                          {item.agent_id || '-'}
                        </td>
                        <td className='py-3 px-2 text-muted-foreground'>
                          {item.feature_id || '-'}
                        </td>
                        <td className='py-3 px-2 text-right'>
                          {item.quantity}
                        </td>
                        <td className='py-3 px-2 text-right'>
                          {formatCentsAsCurrency(
                            item.unit_price_cents,
                            currency
                          )}
                        </td>
                        <td className='py-3 px-2 text-right'>
                          {item.discount_cents
                            ? formatCentsAsCurrency(
                                item.discount_cents,
                                currency
                              )
                            : '-'}
                        </td>
                        <td className='py-3 px-2 text-right font-medium'>
                          {formatCentsAsCurrency(item.total_cents, currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Summary */}
              <div className='mt-6 pt-4 border-t space-y-2'>
                <div className='flex justify-between text-sm'>
                  <span>Subtotal</span>
                  <span>
                    {formatCentsAsCurrency(invoice.subtotal_cents, currency)}
                  </span>
                </div>
                {invoice.tax_cents > 0 && (
                  <div className='flex justify-between text-sm'>
                    <span>
                      Tax
                      {invoice.tax_rate
                        ? ` (${(invoice.tax_rate * 100).toFixed(1)}%)`
                        : ''}
                    </span>
                    <span>
                      {formatCentsAsCurrency(invoice.tax_cents, currency)}
                    </span>
                  </div>
                )}
                {invoice.discount_cents > 0 && (
                  <div className='flex justify-between text-sm text-green-600'>
                    <span>Discount</span>
                    <span>
                      -{formatCentsAsCurrency(invoice.discount_cents, currency)}
                    </span>
                  </div>
                )}
                <div className='flex justify-between font-bold text-lg border-t pt-2'>
                  <span>Total</span>
                  <span>
                    {formatCentsAsCurrency(invoice.total_cents, currency)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          {invoice.notes && (
            <Card>
              <CardHeader className='pb-3'>
                <CardTitle className='text-sm font-medium'>Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className='text-sm text-muted-foreground'>{invoice.notes}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column — Actions & Info (35%) */}
        <div className='lg:col-span-2 space-y-6'>
          {/* Status Actions */}
          <InvoiceStatusActions
            invoice={invoice}
            onFinalize={handleFinalize}
            onRecordPayment={() => setShowRecordPayment(true)}
            onMarkAsPaid={handleMarkAsPaid}
            onVoid={handleVoid}
            loading={loading}
          />

          {/* Payment Summary */}
          <Card>
            <CardHeader className='pb-3'>
              <CardTitle className='text-sm font-medium'>
                Payment Summary
              </CardTitle>
            </CardHeader>
            <CardContent className='space-y-3'>
              <div className='flex justify-between text-sm'>
                <span>Total</span>
                <span className='font-medium'>
                  {formatCentsAsCurrency(invoice.total_cents, currency)}
                </span>
              </div>
              <div className='flex justify-between text-sm'>
                <span>Paid</span>
                <span className='font-medium text-green-600'>
                  {formatCentsAsCurrency(invoice.amount_paid_cents, currency)}
                </span>
              </div>
              <div className='flex justify-between text-sm border-t pt-2'>
                <span className='font-medium'>Remaining</span>
                <span className='font-bold'>
                  {formatCentsAsCurrency(invoice.amount_due_cents, currency)}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Download PDF */}
          <Button
            variant='outline'
            className='w-full gap-2'
            onClick={() => downloadPdf(invoice)}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <Loader2 className='w-4 h-4 animate-spin' />
            ) : (
              <Download className='w-4 h-4' />
            )}
            {isGenerating ? 'Generating...' : 'Download PDF'}
          </Button>

          {/* Dates */}
          <Card>
            <CardHeader className='pb-3'>
              <CardTitle className='text-sm font-medium'>Dates</CardTitle>
            </CardHeader>
            <CardContent className='space-y-3'>
              <div className='flex justify-between text-sm'>
                <span className='text-muted-foreground flex items-center gap-1'>
                  <Calendar className='w-3 h-3' />
                  Issue Date
                </span>
                <span>{formatInvoiceDate(invoice.issue_date)}</span>
              </div>
              {invoice.due_date && (
                <div className='flex justify-between text-sm'>
                  <span className='text-muted-foreground flex items-center gap-1'>
                    <Calendar className='w-3 h-3' />
                    Due Date
                  </span>
                  <span>{formatInvoiceDate(invoice.due_date)}</span>
                </div>
              )}
              {invoice.billing_period_from && invoice.billing_period_to && (
                <div className='flex justify-between text-sm'>
                  <span className='text-muted-foreground'>Billing Period</span>
                  <span>
                    {formatInvoiceDate(invoice.billing_period_from)} –{' '}
                    {formatInvoiceDate(invoice.billing_period_to)}
                  </span>
                </div>
              )}
              <div className='flex justify-between text-sm'>
                <span className='text-muted-foreground'>Created</span>
                <span>{formatInvoiceDate(invoice.created_at)}</span>
              </div>
              <div className='flex justify-between text-sm'>
                <span className='text-muted-foreground'>Updated</span>
                <span>{formatInvoiceDate(invoice.updated_at)}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Record Payment Dialog */}
      <RecordPaymentDialog
        invoice={invoice}
        open={showRecordPayment}
        onOpenChange={setShowRecordPayment}
        onSuccess={onInvoiceUpdated}
      />
    </>
  )
}
